import { useCallback, useEffect, useRef, useState } from 'react';
import { ScanKind } from './monitorTokens';

const STORAGE_KEY = 'monitor.soundEnabled';

// Short synthesized tones so the operator hears the outcome without looking at the screen.
// Generated with WebAudio, so there are no audio files to ship or cache.
const PATTERNS: Record<ScanKind, Array<{ freq: number; ms: number }>> = {
    delivered: [{ freq: 880, ms: 90 }, { freq: 1320, ms: 140 }],
    already: [{ freq: 520, ms: 220 }],
    noReservation: [{ freq: 240, ms: 160 }, { freq: 200, ms: 220 }],
    invalidToken: [{ freq: 240, ms: 160 }, { freq: 200, ms: 220 }],
    undone: [{ freq: 990, ms: 110 }, { freq: 660, ms: 160 }],
    notFound: [{ freq: 240, ms: 160 }, { freq: 200, ms: 220 }],
    unknownCard: [{ freq: 660, ms: 120 }, { freq: 660, ms: 120 }],
    error: [{ freq: 180, ms: 350 }],
};

const readStored = () => {
    try {
        return localStorage.getItem(STORAGE_KEY) !== 'false';
    } catch {
        return true;
    }
};

export const useScanSound = () => {
    const [enabled, setEnabled] = useState<boolean>(readStored);
    const ctxRef = useRef<AudioContext | null>(null);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, String(enabled));
        } catch {
            // storage can be unavailable (private mode); the toggle still works for this session
        }
    }, [enabled]);

    useEffect(() => () => { ctxRef.current?.close().catch(() => undefined); }, []);

    const play = useCallback((kind: ScanKind) => {
        if (!enabled) return;
        try {
            const Ctor = window.AudioContext || (window as any).webkitAudioContext;
            if (!Ctor) return;
            const ctx = ctxRef.current ?? (ctxRef.current = new Ctor());
            if (ctx.state === 'suspended') ctx.resume().catch(() => undefined);

            let start = ctx.currentTime;
            for (const { freq, ms } of PATTERNS[kind]) {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.value = freq;
                const end = start + ms / 1000;
                gain.gain.setValueAtTime(0.0001, start);
                gain.gain.exponentialRampToValueAtTime(0.25, start + 0.01);
                gain.gain.exponentialRampToValueAtTime(0.0001, end);
                osc.connect(gain).connect(ctx.destination);
                osc.start(start);
                osc.stop(end + 0.02);
                start = end + 0.04;
            }
        } catch {
            // audio is a nicety; never let it break a delivery
        }
    }, [enabled]);

    return { soundEnabled: enabled, setSoundEnabled: setEnabled, playScanSound: play };
};
